import { Component, signal, ViewChild, computed, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { JSONEditor } from '@json-editor/json-editor';
import { Store } from '@ngrx/store';
import { Apollo, gql } from 'apollo-angular';
import { selectDocumentStateIsDirty, selectInstances, updateDocumentState } from 'editor';
import * as yaml from 'js-yaml';
import { BehaviorSubject, combineLatest, firstValueFrom, merge } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import * as ExportActions from '../store/export.actions';
import { selectMetadata, selectTemplate } from '../store/export.reducer';
import { MatSelect } from '@angular/material/select';
import { DcaStore } from 'shared';
import { EXPORT_LOCALES } from './export-locales';
import { UI_VISIBILITY_ROLES } from './export-settings-config';

const getActiveSettings = (
  editorId: string,
): { templateSlug?: string; locale?: string } | undefined => {
  try {
    const value = sessionStorage.getItem(editorId);
    if (value) {
      return JSON.parse(value as string) as { templateSlug?: string; locale?: string };
    }
  } catch (e) {
    console.error(e);
  }
  return undefined;
};

const getActiveTemplate = (editorId: string) => getActiveSettings(editorId)?.templateSlug;

@Component({
  selector: 'lib-export',
  templateUrl: './export.component.html',
  styleUrls: ['./export.component.scss'],
  standalone: false,
})
export class ExportComponent {
  private store: Store = inject(Store);
  private apollo: Apollo = inject(Apollo);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private router: Router = inject(Router);
  private snackBar: MatSnackBar = inject(MatSnackBar);
  private dcaStore: DcaStore = inject(DcaStore);

  @ViewChild('metaData') metaDataEl;
  @ViewChild('locale') locale: MatSelect;
  @ViewChild('citationStyle') citationStyle: MatSelect;

  loading$ = new BehaviorSubject(false);
  metaDataEditorDirty$ = new BehaviorSubject(false);
  metaDataEditor: JSONEditor;

  templates$ = this.apollo
    .query({
      query: gql`query GetTemplates {
        templates {
          slug
          type
          title
          description
          hidden
        }
      }`,
      variables: {},
    })
    .pipe(
      map((result: any) => result?.data?.templates || []),
      shareReplay(1),
    );

  templateSlug$ = this.activatedRoute.params.pipe(
    map((p: any) => p.template),
    startWith(this.activatedRoute.snapshot.params?.value?.template),
  );

  activeEditor$ = this.store.select(selectInstances).pipe(
    filter((instances) => instances.length > 0),
    map((instances) => instances[0]),
  );

  activeTemplate$ = combineLatest([
    merge(
      this.activeEditor$.pipe(
        filter(() => this.activatedRoute.snapshot.params.template === undefined),
        map((editor) => editor.id && getActiveTemplate(editor.id)),
        tap(console.log),
      ),
      this.templateSlug$.pipe(
        startWith(this.activatedRoute.snapshot.params.template),
        debounceTime(100),
        filter((v) => v != undefined),
      ),
    ).pipe(distinctUntilChanged()),
    this.templates$,
  ]).pipe(
    map(([templateSlug, templates]) => {
      if (templateSlug && templates?.some((t: any) => t.slug === templateSlug)) {
        return templateSlug;
      }
      return undefined;
    }),
    distinctUntilChanged(),
    switchMap((template: string | undefined) => this.loadTemplate(template)),
    shareReplay(1),
  );

  data$ = combineLatest([this.activeEditor$, this.activeTemplate$, this.templates$]).pipe(
    map(([editor, template, templates]) => ({ editor, template, templates })),
    tap((data) => {
      if (data.template?.slug) {
        const current = getActiveSettings(data.editor.id) || {};
        const next = {
          ...current,
          templateSlug: data.template.slug,
          ...(this.locale?.value ? { locale: this.locale.value } : {}),
        } as { templateSlug: string; locale?: string };
        sessionStorage?.setItem(data.editor.id, JSON.stringify(next));
      }
    }),
  );

  isDocumentStateDirty$ = this.store.select(selectDocumentStateIsDirty);

  readonly isDcaExpanded = computed(() => this.dcaStore.isDcaExpanded());
  readonly isDcaShown = computed(() => this.dcaStore.isDcaShown());
  readonly isDcaMaximized = computed(() => this.dcaStore.isDcaMaximized());

  locales = EXPORT_LOCALES;
  uiVisibilityRoles = UI_VISIBILITY_ROLES;
  selectedUiRoles: string[] = ['author'];
  hideMultipleSelectionIndicator = signal(false);

  async loadTemplate(template?: string) {
    if (!template) {
      return null;
    }

    this.loading$.next(true);
    this.snackBar.open('Loading template ...');

    const documentResult: any = await this.apollo
      .query({
        query: gql`query GetTemplate($template: String!, $projectId: String) {
        template(templateId: $template, projectId: $projectId) {
          slug
          title
          description
          readme
          type
          metaData
          configurations
          runners
          prosemirrorSchemas
        }
      }`,
        variables: {
          template,
        },
      })
      .toPromise();

    this.loading$.next(false);
    this.snackBar.open('Loaded ' + documentResult.data?.template?.title, undefined, {
      duration: 2000,
    });

    const configuration = documentResult.data.template?.configurations.find(
      (config) => config.kind === 'Configuration',
    )?.spec;

    const language = configuration?.language;
    const languageAlternatives = configuration?.languageAlternatives ?? [];
    const locales = [language, ...languageAlternatives]
      .filter((l) => l != undefined)
      .map((locale) => this.locales.find((l2) => l2.value === locale));
    const citationStyle = configuration?.citationStyle;
    const citationStyleAlternatives = configuration?.citationStyleAlternative || [];
    const citationStyles = [citationStyle, ...citationStyleAlternatives].filter(
      (l) => l != undefined,
    );

    const templateObj = {
      locales,
      citationStyles,
      ...documentResult.data.template,
    };
    console.table('loaded template', templateObj);

    this.store.dispatch(ExportActions.updateTemplate({ template: templateObj }));

    return templateObj;
  }

  hasLabel(template, label: string) {
    const configuration = template?.configurations?.find((c) => c.kind === 'Configuration');
    if (!template || !configuration?.spec) {
      return false;
    }
    return configuration?.spec.labels?.includes(label);
  }

  compareTemplate(template, slug: string) {
    return template === slug;
  }

  canEdit(key?: string) {
    return key?.includes('.json');
  }

  getInitialLocale(
    locales: { value: string }[] | undefined,
    editorId: string | undefined,
  ): string | undefined {
    if (!locales?.length || !editorId) return locales?.[0]?.value;
    const stored = getActiveSettings(editorId)?.locale;
    return stored && locales.some((l) => l.value === stored) ? stored : locales[0]?.value;
  }

  onLocaleChange(newLocale: string, editorId: string | undefined): void {
    if (!editorId) return;
    const current = getActiveSettings(editorId) || {};
    const next = { ...current, locale: newLocale } as { templateSlug?: string; locale: string };
    sessionStorage?.setItem(editorId, JSON.stringify(next));
  }

  onToggleChange(values: string[]) {
    this.selectedUiRoles = values;
  }

  toggleMultipleSelectionIndicator() {
    this.hideMultipleSelectionIndicator.update((value) => !value);
  }

  async navigateToTemplate(template: string) {
    this.activatedRoute.snapshot.params.template
      ? this.router.navigate(['../' + template], { relativeTo: this.activatedRoute })
      : this.router.navigate([template], { relativeTo: this.activatedRoute });
  }

  toggleMetaData() {
    this.dcaStore.expandDcaWithMaximize();
    this.router.navigate(['setting'], { relativeTo: this.activatedRoute });
  }

  closeMetaDataEditor() {
    this.dcaStore.collaspeDcaExpansion();
  }

  expandSettings() {
    this.dcaStore.expandDcaWithMaximize();
    this.router.navigate(['setting'], { relativeTo: this.activatedRoute });
  }

  resetMetaData() {
    this.store.dispatch(ExportActions.updateMetadata({ metaData: null }));
    this.store.dispatch(updateDocumentState({ dirty: true }));
  }

  async exportWithTemplate(template: string, key?: string, format = 'pagedjs'): Promise<void> {
    if (!key) return;
    const locale = this.locale?.value;
    const citationStyle = this.citationStyle?.value;
    const debug = localStorage.getItem('export.debugPDF.enabled') === 'true';
    const defaultTemplate = (await firstValueFrom(this.data$))?.templates?.[0].slug;

    const url = new URL(
      `/export/${format}/${template ?? defaultTemplate}/${escapeKey(key)}`,
      window.location.origin,
    );
    if (debug) {
      url.searchParams.set('debug', 'true');
      url.searchParams.set('download', 'false');
    }
    if (locale) {
      url.searchParams.set('locale', locale);
    }
    if (citationStyle) {
      url.searchParams.set('citationStyle', citationStyle);
    }
    window.open(url.toString(), '_blank');
  }

  async exportSnapshot(template: string, key: string): Promise<void> {
    if (!key) return;
    const url = new URL(
      `/export/snapshot/${template}/${escapeKey(key)}.zip`,
      window.location.origin,
    );
    const locale = this.locale?.value;
    const citationStyle = this.citationStyle?.value;
    if (locale) {
      url.searchParams.set('locale', locale);
    }
    if (citationStyle) {
      url.searchParams.set('citationStyle', citationStyle);
    }
    window.open(url.toString(), '_blank');
  }

  async exportReaderSnapshot(template: string, key: string): Promise<void> {
    if (!key) return;
    const url = new URL(
      `/export/reader-snapshot/${template}/${escapeKey(key)}.zip`,
      window.location.origin,
    );
    const locale = this.locale?.value;
    const citationStyle = this.citationStyle?.value;
    if (locale) {
      url.searchParams.set('locale', locale);
    }
    if (citationStyle) {
      url.searchParams.set('citationStyle', citationStyle);
    }
    window.open(url.toString(), '_blank');
  }

  async exportOJSSnapshot(template: string, key: string): Promise<void> {
    if (!key) return;
    const url = new URL(
      `/export/ojsxml-zip/${template}/${escapeKey(key)}.zip`,
      window.location.origin,
    );
    const locale = this.locale?.value;
    const citationStyle = this.citationStyle?.value;
    if (locale) {
      url.searchParams.set('locale', locale);
    }
    if (citationStyle) {
      url.searchParams.set('citationStyle', citationStyle);
    }
    window.open(url.toString(), '_blank');
  }

  async openLens(template: string, key?: string, format = 'xml') {
    const url = key && `/export/lens/?url=/export/${format}/${template}/${escapeKey(key)}`;
    url && window.open(url, '_blank');
  }

  async openReader(template: string, key?: string, format = 'reader-snapshot'): Promise<void> {
    if (!key) return;
    const locale = this.locale?.value;
    const inner = new URL(
      `/export/${format}/${template}/${escapeKey(key)}`,
      window.location.origin,
    );
    if (locale) {
      inner.searchParams.set('locale', locale);
    }
    const outer = new URL('/export/reader/', window.location.origin);
    outer.searchParams.set('url', `${inner.pathname}${inner.search}`);
    window.open(outer.toString(), '_blank');
  }

  async downloadTemplate() {
    const template = await firstValueFrom(this.store.select(selectTemplate).pipe(take(1)));
    const metaData = await firstValueFrom(this.store.select(selectMetadata).pipe(take(1)));

    try {
      const newTemplate = this.patchTemplateWithMetadata(metaData, template);

      const yaml = this.convertJsonToYaml({
        ...newTemplate,
        configurations: newTemplate.configurations.map((conf) => {
          if (conf.kind === 'Configuration') {
            conf = {
              ...conf,
              spec: {
                ...(conf.spec || {}),
                assets: (conf.spec?.assets || []).map((asset) => {
                  if (asset?.content) {
                    asset = { ...asset };
                    delete asset.content;
                  }
                  return asset;
                }),
              },
            };
          }
          return conf;
        }),
      });

      await navigator.clipboard.writeText(yaml);
      this.snackBar.open(`Template copied to clipboard.`, 'Close', { duration: 5000 });
    } catch (err) {
      this.snackBar.open(`There was an error generating the template.`, 'Close', {
        duration: 5000,
      });
      console.error('Failed to copy text: ', err);
    }
  }

  private patchTemplateWithMetadata(metadata: any, template: any) {
    if (!metadata) {
      return template;
    }

    const clonedMetadata = structuredClone(metadata);
    const clonedTemplate = structuredClone(template);

    Object.keys(clonedMetadata).forEach((metadataKey) => {
      const metaDataProperty = clonedTemplate.metaData.properties[metadataKey];

      const matchingConfig = clonedTemplate.configurations.find((config: any) => {
        return (
          config.kind === metaDataProperty.component.kind &&
          metaDataProperty.component.runners?.every((runner: string) =>
            config.runners.includes(runner),
          )
        );
      });

      if (matchingConfig) {
        const spec = matchingConfig.spec;
        const metadataValues = clonedMetadata[metadataKey];

        const mergeSpec = (spec: any, metadataValues: any, defaultProperties: any) => {
          Object.keys(metadataValues).forEach((key) => {
            if (defaultProperties && defaultProperties[key]) {
              if (metadataValues[key] === defaultProperties[key].default) {
                return;
              }

              if (typeof metadataValues[key] === 'object' && !Array.isArray(metadataValues[key])) {
                if (!spec[key]) {
                  return;
                }
                mergeSpec(spec[key], metadataValues[key], defaultProperties[key].properties);
              } else {
                spec[key] = metadataValues[key];
              }
            }
          });
        };

        mergeSpec(spec, metadataValues, metaDataProperty.properties);
      }
    });

    return clonedTemplate;
  }

  convertJsonToYaml(jsonObject: any): string {
    let yamlDocuments: string[] = [];

    jsonObject.configurations.forEach((config: any) => {
      let yamlDoc = yaml.dump(config, { sortKeys: false });
      yamlDocuments.push(yamlDoc);
    });

    return yamlDocuments.join('---\n');
  }
}

export const escapeKey = (key: string) => {
  if (!key?.includes('/')) {
    return key;
  }
  const fileArray = key.split('/');
  const projectId = fileArray[0];
  const fileName = fileArray[fileArray.length - 1];
  const prefix = fileArray.slice(1, fileArray.length - 1).join('/');

  if (!prefix) {
    return `${projectId}/${encodeURIComponent(fileName)}`;
  }

  return `${projectId}/${encodeURIComponent(prefix + '/' + fileName)}`;
};
